import { visit } from "unist-util-visit";
import type { Root, Text, Element } from "hast";

export interface HighlightEntry {
  id: string;
  text: string;
  state: "selected" | "focused" | "unselected";
  charOffset?: number; // character offset in full text for unique anchoring
}

const STATE_CLASSES: Record<HighlightEntry["state"], string> = {
  selected: "bg-amber-300/50 dark:bg-amber-700/50",
  focused: "bg-amber-300/40 dark:bg-amber-700/40 ring-2 ring-amber-400 animate-pulse",
  unselected: "bg-amber-300/25 dark:bg-amber-700/25",
};

interface Options {
  highlights: HighlightEntry[];
}

export function rehypeHighlights(options: Options) {
  const { highlights } = options;
  if (!highlights.length) return (tree: Root) => tree;

  // Sort longest first to prevent partial overlaps
  const sorted = [...highlights].sort((a, b) => b.text.length - a.text.length);

  return (tree: Root) => {
    // Track running character offset across all text nodes for charOffset matching
    let globalOffset = 0;

    visit(tree, "text", (node: Text, index, parent) => {
      if (!parent || index == null) return;
      if ((parent as Element).tagName === "mark") return; // don't nest marks

      const nodeText = node.value;
      const nodeLower = nodeText.toLowerCase();
      const nodeStart = globalOffset;
      const nodeEnd = nodeStart + nodeText.length;

      type Match = { start: number; end: number; entry: HighlightEntry };
      const matches: Match[] = [];

      for (const entry of sorted) {
        const needle = entry.text.toLowerCase();
        if (!needle) continue;

        if (entry.charOffset != null) {
          // Anchored match: only match if this node contains the target offset
          const targetStart = entry.charOffset;
          if (targetStart >= nodeStart && targetStart < nodeEnd) {
            const localStart = targetStart - nodeStart;
            const localEnd = Math.min(localStart + needle.length, nodeText.length);
            // Verify text actually matches at this offset
            if (nodeLower.slice(localStart, localEnd) === needle.slice(0, localEnd - localStart)) {
              const overlaps = matches.some((m) => localStart < m.end && localEnd > m.start);
              if (!overlaps) {
                matches.push({ start: localStart, end: localEnd, entry });
              }
            }
          }
        } else {
          // Unanchored: match first occurrence (legacy highlights without offset)
          const idx = nodeLower.indexOf(needle);
          if (idx >= 0) {
            const end = idx + needle.length;
            const overlaps = matches.some((m) => idx < m.end && end > m.start);
            if (!overlaps) {
              matches.push({ start: idx, end, entry });
            }
          }
        }
      }

      globalOffset = nodeEnd;

      if (!matches.length) return;

      matches.sort((a, b) => a.start - b.start);

      const newNodes: (Text | Element)[] = [];
      let cursor = 0;

      for (const match of matches) {
        if (match.start > cursor) {
          newNodes.push({ type: "text", value: nodeText.slice(cursor, match.start) });
        }
        newNodes.push({
          type: "element",
          tagName: "mark",
          properties: {
            "data-highlight-id": match.entry.id,
            className: STATE_CLASSES[match.entry.state],
          },
          children: [{ type: "text", value: nodeText.slice(match.start, match.end) }],
        });
        cursor = match.end;
      }

      if (cursor < nodeText.length) {
        newNodes.push({ type: "text", value: nodeText.slice(cursor) });
      }

      parent.children.splice(index, 1, ...newNodes);
    });
  };
}
